import { useCallback, useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import Collapsible, {
    COLLAPSIBLE_TRIGGER_ICON_VARIANT,
    ICON_POSITION,
} from '../../../../../components/collapsible/collapsible';
import FontBody2 from '../../../../../components/fonts/font-body2/font-body2';
import { FONT_COLOR, FONT_VARIANT } from '../../../../../components/fonts/font-props';
import { Input } from '../../../../../components/inputs/input/input';
import { ButtonIcon } from '../../../../../components/inputs/button-icon/button-icon';
import IconEdit from '../../../../../components/icons/icon-edit/icon-edit';
import IconCopy from '../../../../../components/icons/icon-copy/icon-copy';
import { IValidationGroup } from '../../../../../hooks/validation-group/validation-group-hook';
import { Note, NOTE_TYPE } from '../../../../../components/note/note';
import { GLOBAL_E_REASON_CODE_MAX_LENGTH } from '../../../constants';
import ReasonStore from '../../../stores/reason-store';
import reasonListStore from '../../../stores/reason-list-store';
import useTranslation from '../../../../../translation/hooks/translation-hook';
import Translation from '../../../../../translation/translation';
import ReasonCodeCollapsibleHeader from './reason-code-collapsible-header';
import './external-integration-section.scss';

interface ExternalIntegrationSectionProps {
    reason: ReasonStore;
    validationGroup: IValidationGroup;
}

const ExternalIntegrationSection: React.FC<ExternalIntegrationSectionProps> = observer(({
    reason,
    validationGroup,
}) => {
    const { getTranslation } = useTranslation();
    const [isExpanded, setIsExpanded] = useState(false);
    const [isReasonCodeEditMode, setIsReasonCodeEditMode] = useState(!reason.isSavedOnServer);

    useEffect(() => {
        setIsReasonCodeEditMode(!reason.isSavedOnServer);
    }, [reason.id, reason.isSavedOnServer]);

    const currentCode = reason.globalECode ?? '';
    const hasGlobalECodeDraft = reason.hasGlobalECodeChanges();
    const showEditWarning = isExpanded && (isReasonCodeEditMode || hasGlobalECodeDraft);
    // New reasons: always editable. Saved reasons: readonly until the user clicks Edit.
    const showEditableInput = !reason.isSavedOnServer || isReasonCodeEditMode;

    const startReasonCodeEdit = () => {
        setIsExpanded(true);
        setIsReasonCodeEditMode(true);
    };

    const handleToggle = (expanded: boolean) => {
        setIsExpanded(expanded);
        if (!expanded && reason.isSavedOnServer && !hasGlobalECodeDraft) {
            // Exit edit mode on collapse only when there is nothing unsaved to continue editing.
            setIsReasonCodeEditMode(false);
        }
    };

    const copyReasonCode = () => {
        if (!currentCode) {
            return;
        }

        void navigator.clipboard.writeText(currentCode);
    };

    // Backend flag: code was generated by ReturnGO, not set manually by the merchant.
    const showAutoGeneratedBadge = reason.globalECodeIsAutoGenerated && !!currentCode;

    const otherReasonCodesKey = reasonListStore.returnReasons
        .map(other => `${other.id}\0${other.savedGlobalECode ?? other.globalECode ?? ''}`)
        .join('\n');

    const validationCallback = useCallback(
        () => {
            const result = reason.validateGlobalECode(isReasonCodeEditMode);

            return {
                isValid: result.isValid,
                messages: result.messages.map(messageKey => getTranslation(messageKey)),
            };
        },
        [reason, currentCode, isReasonCodeEditMode, otherReasonCodesKey, getTranslation],
    );

    return (
        <div className="external-integration-section">
            <FontBody2 color={FONT_COLOR.PRIMARY3} variant={FONT_VARIANT.BOLD}>
                External Integration
            </FontBody2>

            <div className="external-integration-card">
                <Collapsible
                    isExpanded={isExpanded}
                    onToggle={handleToggle}
                    iconVariant={COLLAPSIBLE_TRIGGER_ICON_VARIANT.ARROW2}
                    iconPosition={ICON_POSITION.RIGHT}
                    title={(
                        <ReasonCodeCollapsibleHeader
                            code={currentCode}
                            showAutoGeneratedBadge={showAutoGeneratedBadge}
                            isExpanded={isExpanded}
                        />
                    )}
                >
                    <div className="external-integration-expanded">
                        <FontBody2 color={FONT_COLOR.NEUTRAL3}>
                        Identifies this reason in your warehouse, order management, or ERP system. ReturnGO generates a code automatically. Only change it if your other systems require a specific value.
                        </FontBody2>

                        <div className="reason-code-input-row">
                            {showEditableInput ? (
                                <div className="reason-code-field">
                                    <Input
                                        value={currentCode}
                                        onChange={val => reason.setGlobalECode(val)}
                                        placeholder="Reason code"
                                        maxlength={GLOBAL_E_REASON_CODE_MAX_LENGTH}
                                        validationGroup={validationGroup}
                                        validationCallback={validationCallback}
                                        showValidationMessage
                                    />
                                </div>
                            ) : (
                                <>
                                    <div className="reason-code-field reason-code-field--readonly">
                                        <Input
                                            value={currentCode}
                                            disabled
                                        />
                                    </div>
                                    <ButtonIcon
                                        Component={() => <IconEdit color="var(--color-accent-1)" />}
                                        onClick={startReasonCodeEdit}
                                    />
                                    <ButtonIcon
                                        Component={() => <IconCopy color="var(--color-accent-1)" />}
                                        onClick={copyReasonCode}
                                        disabled={!currentCode}
                                    />
                                </>
                            )}
                        </div>

                        {showEditWarning && (
                            <Note
                                type={NOTE_TYPE.WARNING}
                                body={"Changing this code affects how new returns are matched in your connected systems. Returns already created are not affected."}
                            />
                        )}
                    </div>
                </Collapsible>
            </div>
        </div>
    );
});

export default ExternalIntegrationSection;
